(() => {
  'use strict';

  const forms = document.querySelectorAll('form[id^="collection-top-bar-filters-"]');
  forms.forEach(initForm);

  function initForm(form) {
    appendProductsHashToAction(form);
    applyUrlPillState(form);
    wireChips(form);
    wirePillGroups(form);
    wireBrandResponsive(form);
    wirePriceSelect(form);
    wireFormAutoSubmit(form);
  }

  // Append #products to the form action once so every GET submit navigates to
  // /collections/<x>?<filters>#products — browser then auto-scrolls to the anchor
  // at the bottom of the top-bar section, landing the user at the start of the
  // product grid instead of the top of the page. Browsers preserve the action's
  // fragment when serializing a GET form's query string.
  function appendProductsHashToAction(form) {
    if (!form.action || form.action.indexOf('#') !== -1) return;
    form.action = form.action + '#products';
  }

  // Brand has two UI variants in the DOM — a desktop <details> chip with multi-checkbox panel
  // and a mobile native <select>. CSS hides one based on viewport. Both render with name="filter.p.vendor"
  // by default, which would cause both to submit. This handler removes the `name` attribute from
  // whichever variant is hidden so only the visible one contributes to the URL on form submit.
  function wireBrandResponsive(form) {
    const desktopChip = form.querySelector('.collection-top-bar__chip[data-chip="brand"]');
    const mobileSelect = form.querySelector('select[data-brand-select]');
    if (!desktopChip || !mobileSelect) return;
    const desktopInputs = () => desktopChip.querySelectorAll('input[type="checkbox"]');
    const mq = window.matchMedia('(max-width: 700px)');
    function applyViewport() {
      if (mq.matches) {
        desktopInputs().forEach(cb => cb.removeAttribute('name'));
        if (mobileSelect.value) mobileSelect.setAttribute('name', 'filter.p.vendor');
        else mobileSelect.removeAttribute('name');
      } else {
        desktopInputs().forEach(cb => cb.setAttribute('name', 'filter.p.vendor'));
        mobileSelect.removeAttribute('name');
      }
    }
    applyViewport();
    mq.addEventListener('change', applyViewport);
    // "All brands" option value is "" — strip name so an empty param doesn't pollute the URL.
    mobileSelect.addEventListener('change', () => {
      if (!mq.matches) return;
      if (mobileSelect.value) mobileSelect.setAttribute('name', 'filter.p.vendor');
      else mobileSelect.removeAttribute('name');
    });
  }

  // Mutually-exclusive pill groups: when a pill with data-pill-group="X" is checked,
  // uncheck every other pill with the same group token. Must run before wireFormAutoSubmit
  // so the unchecks are reflected in the submitted form data (same change event triggers both).
  function wirePillGroups(form) {
    form.addEventListener('change', (e) => {
      const input = e.target;
      if (!input || !input.classList || !input.classList.contains('collection-top-bar__pill-input')) return;
      if (!input.checked) return;
      const label = input.closest('.collection-top-bar__pill');
      if (!label) return;
      const group = label.getAttribute('data-pill-group');
      if (!group) return;
      form.querySelectorAll('.collection-top-bar__pill').forEach(other => {
        if (other === label) return;
        if (other.getAttribute('data-pill-group') !== group) return;
        const otherInput = other.querySelector('.collection-top-bar__pill-input');
        if (otherInput) otherInput.checked = false;
        other.classList.remove('is-active');
      });
    });
  }

  // Mark pills active by parsing window.location.search. Shopify's `collection.filters`
  // exposes filter.p.tag and filter.p.vendor (handled server-side in Liquid) but not
  // metafield filters (filter.p.m.*); `request.query_string` renders blank on this storefront,
  // so server-side detection isn't possible for metafield pills. JS fills the gap on load.
  // Setting input.checked programmatically does not fire 'change', so this won't trigger
  // wireFormAutoSubmit and won't cause a resubmit loop.
  function applyUrlPillState(form) {
    const params = new URLSearchParams(window.location.search);
    form.querySelectorAll('.collection-top-bar__pill').forEach(label => {
      const input = label.querySelector('.collection-top-bar__pill-input');
      if (!input) return;
      if (params.getAll(input.name).includes(input.value)) {
        label.classList.add('is-active');
        input.checked = true;
      }
    });
  }

  // Brand chip (the only remaining <details>-based chip) opens/closes; click-outside + Escape close it.
  function wireChips(form) {
    const chips = Array.from(form.querySelectorAll('.collection-top-bar__chip'));
    if (chips.length === 0) return;
    let previouslyFocused = null;

    chips.forEach(chip => {
      const summary = chip.querySelector('.collection-top-bar__chip-button');
      chip.addEventListener('toggle', () => {
        if (summary) summary.setAttribute('aria-expanded', chip.open ? 'true' : 'false');
        if (chip.open) {
          const anotherWasOpen = chips.some(c => c !== chip && c.open);
          if (!anotherWasOpen) previouslyFocused = document.activeElement;
          chips.forEach(other => { if (other !== chip) other.open = false; });
          const firstInput = chip.querySelector('.collection-top-bar__chip-panel input, .collection-top-bar__chip-panel button');
          if (firstInput) firstInput.focus();
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.collection-top-bar__chip')) {
        const anyWasOpen = chips.some(c => c.open);
        chips.forEach(c => { c.open = false; });
        if (anyWasOpen && previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const anyWasOpen = chips.some(c => c.open);
        chips.forEach(c => { c.open = false; });
        if (anyWasOpen && previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
      }
    });
  }

  // Price <select>: option value is "gte|lte" — split into the hidden filter inputs on change.
  // "All prices" has value "|" (both empty) — strip the inputs' names so empty params don't pollute
  // the URL (Shopify treats `filter.v.price.gte=&filter.v.price.lte=` as filter-to-empty-range).
  function wirePriceSelect(form) {
    const priceSelect = form.querySelector('select[data-price-select]');
    const gteInput = form.querySelector('input[data-price-gte]') || form.querySelector('input[name="filter.v.price.gte"]');
    const lteInput = form.querySelector('input[data-price-lte]') || form.querySelector('input[name="filter.v.price.lte"]');
    if (!priceSelect || !gteInput || !lteInput) return;
    priceSelect.addEventListener('change', () => {
      const [gte, lte] = (priceSelect.value || '|').split('|');
      gteInput.value = gte || '';
      lteInput.value = lte || '';
      if (!gte && !lte) {
        gteInput.removeAttribute('name');
        lteInput.removeAttribute('name');
      } else {
        gteInput.setAttribute('name', 'filter.v.price.gte');
        lteInput.setAttribute('name', 'filter.v.price.lte');
      }
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  }

  function wireFormAutoSubmit(form) {
    form.addEventListener('change', (e) => {
      const tgt = e.target;
      if (!tgt) return;
      // Price select has its own handler (wirePriceSelect).
      if (tgt.matches('select[data-price-select]')) return;
      if (tgt.type === 'checkbox' || tgt.type === 'radio' || tgt.tagName === 'SELECT') {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }
    });
  }
})();
